import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const LocationContext = createContext();

export const useLocations = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocations must be used within LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to locations in real-time
  useEffect(() => {
    const q = query(collection(db, 'locations'), orderBy('name'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const locationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLocations(locationsList);

      // Set default location if not set
      if (!currentLocation && locationsList.length > 0) {
        const defaultLoc = locationsList.find(l => l.isDefault) || locationsList[0];
        setCurrentLocation(defaultLoc);
      }

      setLoading(false);
    }, (error) => {
      console.error('Error fetching locations:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentLocation]);

  // Add new location
  const addLocation = async (locationData) => {
    try {
      const newLocation = {
        ...locationData,
        isActive: true,
        isDefault: locations.length === 0, // First location is default
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        stats: {
          totalOrders: 0,
          totalRevenue: 0,
          totalCustomers: 0
        }
      };

      const docRef = await addDoc(collection(db, 'locations'), newLocation);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding location:', error);
      return { success: false, error };
    }
  };

  // Update location
  const updateLocation = async (locationId, updates) => {
    try {
      const locationRef = doc(db, 'locations', locationId);
      await updateDoc(locationRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating location:', error);
      return { success: false, error };
    }
  };

  // Delete location
  const deleteLocation = async (locationId) => {
    try {
      // Don't allow deleting the default location
      const location = locations.find(l => l.id === locationId);
      if (location?.isDefault) {
        return { success: false, error: 'Cannot delete default location' };
      }

      await deleteDoc(doc(db, 'locations', locationId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting location:', error);
      return { success: false, error };
    }
  };

  // Set default location
  const setDefaultLocation = async (locationId) => {
    try {
      // Remove default from all locations
      const updates = locations.map(loc =>
        updateDoc(doc(db, 'locations', loc.id), { isDefault: loc.id === locationId })
      );
      await Promise.all(updates);
      return { success: true };
    } catch (error) {
      console.error('Error setting default location:', error);
      return { success: false, error };
    }
  };

  // Toggle location active status
  const toggleLocationStatus = async (locationId, currentStatus) => {
    return updateLocation(locationId, { isActive: !currentStatus });
  };

  // Switch current location context
  const switchLocation = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    if (location) {
      setCurrentLocation(location);
      localStorage.setItem('currentLocationId', locationId);
    }
  };

  // Get active locations
  const activeLocations = useMemo(() => {
    return locations.filter(loc => loc.isActive);
  }, [locations]);

  // Statistics across all locations
  const stats = useMemo(() => {
    return {
      total: locations.length,
      active: locations.filter(l => l.isActive).length,
      inactive: locations.filter(l => !l.isActive).length,
      totalOrders: locations.reduce((sum, l) => sum + (l.stats?.totalOrders || 0), 0),
      totalRevenue: locations.reduce((sum, l) => sum + (l.stats?.totalRevenue || 0), 0)
    };
  }, [locations]);

  const value = {
    locations,
    activeLocations,
    currentLocation,
    loading,
    stats,
    addLocation,
    updateLocation,
    deleteLocation,
    setDefaultLocation,
    toggleLocationStatus,
    switchLocation
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
